import { Info, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CountryFlag } from '@/pages/markets/components/CountryFlags';

import { checkboxClass, type ExclusionRow } from './lib';

const MAX_FLAGS = 4;

type Props = {
  rows: ExclusionRow[];
  isLoading: boolean;
  isError: boolean;
  skeletonRows: number;
  selected: ReadonlySet<string>;
  onSelectedChange: (selected: Set<string>) => void;
  onToggleStatus: (row: ExclusionRow, isActive: boolean) => void;
  /** Product IDs whose status toggles are being saved (switch disabled meanwhile). */
  togglingIds: ReadonlySet<string>;
  onEdit: (row: ExclusionRow) => void;
};

export function ExclusionsTable({
  rows,
  isLoading,
  isError,
  skeletonRows,
  selected,
  onSelectedChange,
  onToggleStatus,
  togglingIds,
  onEdit,
}: Props) {
  const { t } = useTranslation('excluded-products');

  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.productId));

  const toggleAll = (checked: boolean) => {
    onSelectedChange(checked ? new Set(rows.map((row) => row.productId)) : new Set());
  };

  const toggleRow = (productId: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(productId);
    else next.delete(productId);
    onSelectedChange(next);
  };

  const headerCell =
    'h-10 border-b px-2 text-left align-middle text-xs font-medium leading-none text-foreground';

  return (
    <div className="overflow-hidden rounded-md border bg-white">
      <table className="w-full table-fixed">
        <thead>
          <tr>
            <th className="h-10 w-9 border-b px-2 text-center align-middle">
              <Checkbox
                className={checkboxClass}
                checked={allSelected}
                onCheckedChange={(checked) => toggleAll(checked === true)}
                aria-label={t('table.selectAll')}
              />
            </th>
            <th className={`${headerCell} w-[151px]`}>{t('columns.productId')}</th>
            <th className={headerCell}>{t('columns.productName')}</th>
            <th className={`${headerCell} w-[205px]`}>{t('columns.excludedMethods')}</th>
            <th className={`${headerCell} w-[134px]`}>{t('columns.excludedMarkets')}</th>
            <th className={`${headerCell} w-[138px]`}>
              <span className="flex items-center gap-1.5">
                {t('columns.status')}
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="size-4 text-[#676767]" strokeWidth={1.5} />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[280px]">{t('status.tooltip')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </span>
            </th>
            <th className="h-10 w-[50px] border-b" />
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            Array.from({ length: skeletonRows }).map((_, index) => (
              <tr key={index} className="border-b last:border-b-0">
                <td className="h-[52px] p-2" colSpan={7}>
                  <Skeleton className="h-5 w-full" />
                </td>
              </tr>
            ))
          ) : isError ? (
            <tr>
              <td colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                {t('table.loadError')}
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                {t('table.empty')}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <ProductRow
                key={row.productId}
                row={row}
                isSelected={selected.has(row.productId)}
                onSelectedChange={(checked) => toggleRow(row.productId, checked)}
                onToggleStatus={onToggleStatus}
                isToggling={togglingIds.has(row.productId)}
                onEdit={onEdit}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

type RowProps = {
  row: ExclusionRow;
  isSelected: boolean;
  onSelectedChange: (checked: boolean) => void;
  onToggleStatus: (row: ExclusionRow, isActive: boolean) => void;
  isToggling: boolean;
  onEdit: (row: ExclusionRow) => void;
};

function ProductRow({
  row,
  isSelected,
  onSelectedChange,
  onToggleStatus,
  isToggling,
  onEdit,
}: RowProps) {
  const { t } = useTranslation('excluded-products');

  const hasExclusion = row.exclusion !== null;
  const methods = row.exclusion?.excluded_methods ?? [];
  const flagCodes = row.exclusion?.excluded_countries ?? [];
  const isActive = row.exclusion?.is_active ?? false;

  return (
    <tr className="border-b last:border-b-0">
      <td className="h-[52px] w-9 p-2 text-center align-middle">
        <Checkbox
          className={checkboxClass}
          checked={isSelected}
          onCheckedChange={(checked) => onSelectedChange(checked === true)}
          aria-label={t('table.selectRow', { name: row.title ?? row.productId })}
        />
      </td>
      <td className="h-[52px] overflow-hidden text-ellipsis whitespace-nowrap p-2 text-base leading-6 text-foreground">
        {row.productId}
      </td>
      <td className="h-[52px] p-2">
        <span className="flex min-w-0 items-center gap-2">
          {row.imageUrl ? (
            <img
              src={row.imageUrl}
              alt=""
              loading="lazy"
              className="size-8 shrink-0 rounded-[4px] border object-cover"
            />
          ) : null}
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-base leading-6 text-foreground">{row.title ?? '-'}</span>
            {row.handle ? (
              <span className="truncate text-xs leading-4 text-muted-foreground">{row.handle}</span>
            ) : null}
          </span>
        </span>
      </td>
      <td className="h-[52px] py-2 pl-2 pr-4">
        {!hasExclusion ? null : methods.length === 0 ? (
          <ScopeAllBadge title={t('scope.allServicesTooltip')} label={t('scope.all')} />
        ) : (
          <span className="flex items-center gap-2">
            <span className="inline-flex h-6 min-w-0 items-center rounded-md bg-[#f5f5f5] px-2 text-sm font-medium leading-5 text-foreground">
              <span className="truncate">{methods[0].label}</span>
            </span>
            {methods.length > 1 ? (
              <span
                className="inline-flex h-6 shrink-0 items-center rounded-md bg-[#f5f5f5] px-2 text-sm font-medium leading-5 text-foreground"
                title={methods
                  .slice(1)
                  .map((method) => method.label)
                  .join(', ')}
              >
                {methods.length - 1}+
              </span>
            ) : null}
          </span>
        )}
      </td>
      <td className="h-[52px] py-2 pl-2 pr-4">
        {!hasExclusion ? null : flagCodes.length === 0 ? (
          <ScopeAllBadge title={t('scope.allMarketsTooltip')} label={t('scope.all')} />
        ) : (
          <span className="flex items-center gap-0.5">
            {/* CountryFlag sets a localized country-name tooltip per flag. Keep its
                default border - this table was the only place stripping it, which
                made the flags read differently here than on markets and widgets. */}
            {flagCodes.slice(0, MAX_FLAGS).map((code) => (
              <CountryFlag key={code} code={code} />
            ))}
            {flagCodes.length > MAX_FLAGS ? (
              <span className="ml-1 text-xs font-medium text-muted-foreground">
                +{flagCodes.length - MAX_FLAGS}
              </span>
            ) : null}
          </span>
        )}
      </td>
      <td className="h-[52px] p-2">
        <span className="flex items-center gap-2.5">
          <Switch
            checked={isActive}
            disabled={isToggling}
            onCheckedChange={(checked) => onToggleStatus(row, checked)}
            aria-label={t('table.statusAria', { name: row.title ?? row.productId })}
          />
          <span className="whitespace-nowrap text-sm font-medium leading-none text-[#171717]">
            {isActive ? t('status.active') : t('status.disabled')}
          </span>
        </span>
      </td>
      <td className="h-[52px] py-2 pl-2 pr-4 text-center align-middle">
        <button
          type="button"
          onClick={() => onEdit(row)}
          aria-label={t('table.editAria', { name: row.title ?? row.productId })}
          className="inline-flex size-9 items-center justify-center rounded-md border border-black bg-white transition-colors hover:bg-[#f5f5f5]"
        >
          <Pencil className="size-5" />
        </button>
      </td>
    </tr>
  );
}

/**
 * An empty scope array is the stored sentinel for "all of them", so a blanket
 * exclusion would otherwise render as two empty cells - indistinguishable from a
 * product with no exclusion at all.
 */
function ScopeAllBadge({ label, title }: { label: string; title: string }) {
  return (
    <span
      title={title}
      className="inline-flex h-6 items-center rounded-md bg-[#f5f5f5] px-2 text-sm font-medium leading-5 text-foreground"
    >
      {label}
    </span>
  );
}
